import { useMemo, useState } from "react";
import { FaCopy, FaCheck } from "react-icons/fa";

const BASE_URL = "https://tools.unclekam.com";

// Attribute definitions with default placeholder values
const ATTR = {
  qa_mode: { name: "qa_mode", default: "true", checkedByDefault: true, description: "Enable QA/staging mode. Uses staging API endpoints instead of production." },
  redirectUrl: { name: "redirectUrl", default: "https://example.com/", description: "URL to redirect the user to after successful form submission." },
  redirecturl: { name: "redirecturl", default: "https://example.com/", description: "URL to redirect the user to after successful form submission." },
  auto_login: { name: "auto_login", default: "true", description: "Automatically log the user in after booking. Defaults to true." },
  pro_id: { name: "pro_id", default: "", description: "Pre-select a specific tax professional by their UUID." },
  theme: { name: "theme", default: "light", type: "select", options: ["light", "dark"], description: 'Color theme for the widget. Accepts "light" or "dark".' },
  data_source: { name: "data_source", default: "VSL", description: "Tracking source identifier for lead attribution (e.g. \"VSL\", \"test\")." },
  productIds: { name: "productIds", default: "1,2", description: "Comma-separated list of product IDs to display (e.g. \"1,2\")." },
  additionalProductIds: { name: "additionalProductIds", default: "1", description: "Comma-separated list of additional/addon product IDs." },
  testing: { name: "testing", default: "true", checkedByDefault: true, description: "Enable test mode. Bypasses real payment processing." },
  productId: { name: "productId", default: "1", description: "The product ID for the upsell offer." },
  upsellPurchasePath: { name: "upsellPurchasePath", default: "", description: "URL path to redirect to after upsell purchase." },
  keyword: { name: "keyword", default: "", description: "Tracking keyword passed to the lead form for campaign attribution." },
  state: { name: "state", default: "NY", description: "Filter tax professionals by US state (e.g. \"NY\", \"CA\")." },
  roundRobin: { name: "roundRobin", default: "true", description: "Enable round-robin assignment of tax professionals." },
  email: { name: "email", default: "", description: "Pre-fill the email address for the redirect action." },
  buttonText: { name: "buttonText", default: "Continue", description: "Custom label text for the redirect button." },
  className: { name: "className", default: "btn btn-primary", description: "Additional CSS class names to apply to the button." },
  redirect_url: { name: "redirect_url", default: "https://example.com/", description: "URL to redirect the user to when the button is clicked." },
  open_in_new_tab: { name: "open_in_new_tab", default: "false", description: "Open the redirect URL in a new tab instead of the same tab." },
  year: { name: "year", default: "2026", description: "Tax year for calculations (e.g. \"2025\", \"2026\")." },
  show_avatar: { name: "show_avatar", default: "true", checkedByDefault: true, description: "Show or hide the profile image." },
  show_badge: { name: "show_badge", default: "true", checkedByDefault: true, description: "Show or hide the rating badge." },
  show_name: { name: "show_name", default: "true", checkedByDefault: true, description: "Show or hide the professional\u2019s name." },
  show_business: { name: "show_business", default: "true", checkedByDefault: true, description: "Show or hide the business name." },
  title_prefix: { name: "title_prefix", default: "Book a Call With", description: "Text displayed before the pro\u2019s name as a title. Leave empty to hide." },
  title_font_size: { name: "title_font_size", default: "24", type: "number", description: "Font size (in px) for the title text." },
  title_color: { name: "title_color", default: "#000000", type: "color", description: "Font color for the title text." },
  offers: { name: "offers", default: "ofr_9bf57ag8,ofr_bxwjn67y", checkedByDefault: true, description: "Comma-separated offer embed codes to display (e.g. \"ofr_9bf57ag8,ofr_bxwjn67y\")." },
  bump_offers: { name: "bump-offers", default: "ofr_bb6zyysg", checkedByDefault: true, description: "Comma-separated bump offer embed codes." },
  select_mode: { name: "select-mode", default: "single", type: "select", options: ["single", "multi"], description: 'Selection mode: "single" (radio) or "multi" (checkboxes).' },
  show_products: { name: "show-products", default: "true", checkedByDefault: true, description: "Display sub-lines for bundled courses and ebooks." },
  show_coupon: { name: "show-coupon", default: "false", description: "Render coupon input field." },
};

// Maps route paths to their widget embed info — exported so App.jsx can read attrs
export const embedMap = {
  // Calculators (all share one bundle)
  "/federal-tax": {
    tagName: "federal-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/business-tax": {
    tagName: "small-business-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/crypto-tax": {
    tagName: "crypto-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/capital-gains": {
    tagName: "capital-gains-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/tax-brackets": {
    tagName: "tax-bracket-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/bonus-tax": {
    tagName: "bonus-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/retirement-savings": {
    tagName: "retirement-savings-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/mortgage-deduction": {
    tagName: "mortgage-interest-deduction-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/self-employment-tax": {
    tagName: "self-employment-tax-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },
  "/home-office": {
    tagName: "home-office-calculator",
    js: "unclekam-calculators.js",
    css: "unclekam-calculators.css",
    attrs: [ATTR.year],
  },

  // Forms
  "/book-appointment": {
    tagName: "appointment-booking-form",
    js: "booking-form.js",
    css: "booking-form.css",
    attrs: [ATTR.qa_mode, ATTR.redirectUrl, ATTR.auto_login],
  },
  "/book-appointment-v2": {
    tagName: "appointment-booking-form-v2",
    js: "booking-form-v2.js",
    css: "booking-form-v2.css",
    attrs: [ATTR.qa_mode, ATTR.redirectUrl, ATTR.auto_login],
  },
  "/book-appointment-v3": {
    tagName: "appointment-booking-form-v3",
    js: "booking-form-v3.js",
    css: "booking-form-v3.css",
    attrs: [ATTR.qa_mode, ATTR.redirecturl, ATTR.auto_login, ATTR.pro_id],
  },
  "/multi-step-lead-generation": {
    tagName: "multi-step-lead-generation",
    js: "multi-step-lead-generation.js",
    css: "multi-step-lead-generation.css",
    attrs: [ATTR.theme, ATTR.qa_mode, ATTR.data_source, ATTR.redirectUrl],
  },
  "/simple-lead-form": {
    tagName: "ghl-lead-form",
    js: "ghlleadform.js",
    css: "ghlleadform.css",
    attrs: [ATTR.theme, ATTR.qa_mode, ATTR.keyword, ATTR.redirectUrl],
  },
  "/one-step-order-form": {
    tagName: "one-step-order-form",
    js: "one-step-order-form.js",
    css: "one-step-order-form.css",
    attrs: [ATTR.productIds, ATTR.additionalProductIds, ATTR.qa_mode, ATTR.redirectUrl, ATTR.testing],
  },
  "/two-step-order-form": {
    tagName: "two-step-order-form",
    js: "two-step-order-form.js",
    css: "two-step-order-form.css",
    attrs: [ATTR.productIds, ATTR.additionalProductIds, ATTR.qa_mode, ATTR.redirectUrl, ATTR.testing],
  },
  "/course-order-form": {
    tagName: "course-order-form",
    js: "course-order-form.js",
    css: "course-order-form.css",
    attrs: [ATTR.offers, ATTR.bump_offers, ATTR.select_mode, ATTR.show_products, ATTR.show_coupon, ATTR.redirectUrl, ATTR.qa_mode, ATTR.testing],
  },
  "/upsell-order-form": {
    tagName: "upsell-order-form",
    js: "upsell-order-form.js",
    css: "upsell-order-form.css",
    attrs: [ATTR.productId, ATTR.upsellPurchasePath, ATTR.redirectUrl, ATTR.qa_mode, ATTR.testing],
  },
  "/lead-generation-form": {
    tagName: "lead-generation",
    js: "lead-generation-form.js",
    css: "lead-generation-form.css",
    attrs: [ATTR.theme, ATTR.qa_mode, ATTR.data_source, ATTR.redirectUrl],
  },
  "/questionnaire-form": {
    tagName: "questionnaire-form",
    js: "questionnaire-form.js",
    css: "questionnaire-form.css",
    attrs: [ATTR.theme, ATTR.qa_mode, ATTR.data_source, ATTR.redirectUrl],
  },
  "/tax-pros": {
    tagName: "taxpro-list",
    js: "taxpros-list.js",
    css: "taxpros-list.css",
    attrs: [ATTR.qa_mode, ATTR.state, ATTR.roundRobin],
  },
  "/redirect-button": {
    tagName: "redirect-button",
    js: "redirect-button.js",
    css: "redirect-button.css",
    attrs: [ATTR.email, ATTR.buttonText, ATTR.className, ATTR.redirect_url, ATTR.qa_mode, ATTR.open_in_new_tab],
  },
  "/pro-card": {
    tagName: "pro-card",
    js: "pro-card.js",
    css: "pro-card.css",
    attrs: [
      { ...ATTR.pro_id, checkedByDefault: true },
      { ...ATTR.qa_mode, checkedByDefault: true },
      ATTR.show_avatar, ATTR.show_badge, ATTR.show_name, ATTR.show_business,
      ATTR.title_prefix, ATTR.title_font_size, ATTR.title_color,
    ],
  },
};

/** Build the initial checked / values maps for a given pathname */
export function initEmbedState(pathname) {
  const info = embedMap[pathname];
  if (!info) return { checked: {}, values: {} };
  const checked = {};
  const values = {};
  for (const attr of info.attrs) {
    checked[attr.name] = !!attr.checkedByDefault;
    values[attr.name] = attr.default;
  }
  return { checked, values };
}

/** Return only checked attributes as a props object (values coerced where appropriate) */
export function getActiveProps(pathname, checked, values) {
  const info = embedMap[pathname];
  if (!info) return {};
  const props = {};
  for (const attr of info.attrs) {
    // For checkedByDefault attrs, always include a value
    if (attr.checkedByDefault) {
      if (checked[attr.name]) {
        // Checked: use the current value, coerced
        const v = values[attr.name] ?? attr.default ?? "";
        if (v === "true") props[attr.name] = true;
        else if (v === "false") props[attr.name] = false;
        else props[attr.name] = v;
      } else {
        // Unchecked: pass false for booleans, empty string for others
        const d = attr.default;
        props[attr.name] = (d === "true" || d === "false") ? false : "";
      }
      continue;
    }
    if (!checked[attr.name]) continue;
    const v = values[attr.name] ?? "";
    // coerce "true"/"false" to booleans, numeric strings to numbers
    if (v === "true") props[attr.name] = true;
    else if (v === "false") props[attr.name] = false;
    else if (v !== "" && !isNaN(Number(v)) && v.trim() !== "") props[attr.name] = Number(v);
    else props[attr.name] = v;
  }
  return props;
}

function buildTag(tagName, attrs, checked, values) {
  const attrStr = attrs
    .filter((a) => {
      if (a.checkedByDefault) {
        const isBooleanAttr = a.default === "true" || a.default === "false";
        // Boolean attrs: include only when unchecked (to pass "false")
        if (isBooleanAttr) return !checked[a.name];
        // Non-boolean attrs (like pro_id): include when checked
        return checked[a.name];
      }
      return checked[a.name];
    })
    .map((a) => {
      if (a.checkedByDefault) {
        const isBooleanAttr = a.default === "true" || a.default === "false";
        if (isBooleanAttr) return `${a.name}="false"`;
        return `${a.name}="${values[a.name] || ""}"`;
      }
      return `${a.name}="${values[a.name] || ""}"`;
    })
    .join(" ");

  const open = attrStr ? `<${tagName} ${attrStr}>` : `<${tagName}>`;
  return `${open}</${tagName}>`;
}

function buildSnippet(tagName, js, css, attrs, checked, values) {
  const tag = buildTag(tagName, attrs, checked, values);
  return `<link rel="stylesheet" href="${BASE_URL}/widgets/${css}" />\n<script src="${BASE_URL}/widgets/${js}"><\/script>\n\n${tag}`;
}

export default function EmbedCodeSection({ pathname, checked, values, onToggle, onValueChange }) {
  const [copied, setCopied] = useState(false);
  const info = embedMap[pathname];
  const attrs = info?.attrs || [];

  const snippet = useMemo(() => {
    if (!info) return "";
    return buildSnippet(info.tagName, info.js, info.css, attrs, checked, values);
  }, [info, attrs, checked, values]);

  if (!info) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="mt-4 border rounded p-3" style={{ backgroundColor: "#f0f0f0" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0 fw-bold">Embed Code</h6>
        <button
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
          onClick={handleCopy}
        >
          {copied ? <FaCheck /> : <FaCopy />}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre
        className="mb-0 p-3 rounded"
        style={{
          backgroundColor: "#1e1e2e",
          color: "#cdd6f4",
          fontSize: "13px",
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        <code>{snippet}</code>
      </pre>

      {attrs.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-bold mb-2" style={{ fontSize: "14px" }}>Attributes</h6>
          <table className="table table-sm table-bordered mb-0 bg-white" style={{ fontSize: "13px" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}></th>
                <th style={{ width: "25%" }}>Attribute</th>
                <th style={{ width: "30%" }}>Value</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {attrs.map((attr) => (
                <tr key={attr.name}>
                  <td className="text-center align-middle">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!checked[attr.name]}
                      onChange={() => onToggle(attr.name)}
                    />
                  </td>
                  <td className="align-middle">
                    <code>{attr.name}</code>
                  </td>
                  <td className="align-middle">
                    {attr.type === "select" ? (
                      <select
                        className="form-select form-select-sm"
                        value={values[attr.name] ?? attr.default ?? ""}
                        disabled={!checked[attr.name]}
                        onChange={(e) => onValueChange(attr.name, e.target.value)}
                      >
                        {attr.options?.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : attr.type === "number" ? (
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={values[attr.name] ?? ""}
                        disabled={!checked[attr.name]}
                        onChange={(e) => onValueChange(attr.name, e.target.value)}
                        placeholder={attr.default || "..."}
                        min="1"
                      />
                    ) : attr.type === "color" ? (
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          style={{ width: 40, height: 34, padding: 2, cursor: "pointer" }}
                          value={values[attr.name] ?? "#000000"}
                          disabled={!checked[attr.name]}
                          onChange={(e) => onValueChange(attr.name, e.target.value)}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={values[attr.name] ?? ""}
                          disabled={!checked[attr.name]}
                          onChange={(e) => onValueChange(attr.name, e.target.value)}
                          placeholder={attr.default || "..."}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={values[attr.name] ?? ""}
                        disabled={!checked[attr.name]}
                        onChange={(e) => onValueChange(attr.name, e.target.value)}
                        placeholder={attr.default || "..."}
                      />
                    )}
                  </td>
                  <td className="align-middle">{attr.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
